import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { viewScanDialogSx } from 'src/common/ViewScanDialogSxs';
import {
  buyAiAddon,
  getCreditDetails,
  getSubscriptionDetailsStripe,
  siteDetail
} from 'src/common/Scraper';
import CommonAlert from 'src/common/CommonAlert';
import { RootState } from 'src/redux/store';
import { setWebsiteMonitoringDetails } from 'src/redux/siteAuditReducer';
import { localStorageMethod } from 'src/common/config/FunctionHelper';
import { UpgradeAgencyPlanModalSxs } from '../onBoarding/UpgradeAgencyPlanModalSx';
import { paidPlanOnBoardingSxs } from '../onBoarding/OnBoardingCSS';
import { setAiAddonMessage, setIsAiAddonPurchased } from 'src/redux/dashboard';
import EditPayment from 'src/content/billing/components/EditPayment';
import CommonModal from 'src/common/config/CommonModal';
import { setStateStripeSubscription } from 'src/redux/userReducer';
import { aiVisibilityContent, paths } from 'src/common/config/TextMessage';
import { upgradeWarningPlanSx } from 'src/common/CommomSxs';
import { updateSearchesDetailsAndLimits } from 'src/common/config/FunctionHelperPartTwo';

const THEME_COLOR = 'var(--color-primary)'; // Dark Blue

const getPricePerSlot = (count: number, timeline: string) => {
  if (count <= 0) return 0;
  if (timeline === 'yearly') {
    if (count >= 1 && count <= 3) return 450;
    if (count >= 4 && count <= 10) return 360;
    // >11
    return 270;
  }
  // monthly
  if (count >= 1 && count <= 3) return 50;
  if (count >= 4 && count <= 10) return 40;
  // >11
  return 30;
};

const MIN_SLOTS = 1;
//const MAX_SLOTS = 50;

export const AiVisibilityAddonModal = ({
  handleClose
}: {
  handleClose: () => void;
}) => {
  let stripeSubscriptionDetails = useSelector(
    (state: RootState) => state.user.subscription
  );
  const siteId = useSelector((state: RootState) => state.siteAudit.siteId) ?? 0;
  let compare = useSelector((state: RootState) => state.siteAudit.compare);
  let scanDetailRedux = useSelector(
    (state: RootState) => state.counter.scanDetails
  );
  let subscription =
    useSelector(
      (state: RootState) => state.counter.userDetails['subscription']
    ) ?? {};
  let userDetails: any =
    useSelector((state: RootState) => state.counter.userDetails) ?? {};
  const isMember = userDetails?.role_type === 'member';
  let searchesDetails: any = useSelector(
    (state: RootState) => state.keywordResearch.searchesDetails
  );
  let planDetails = useSelector((state: RootState) => state.user.planDetails);
  let activePlanName = useSelector((state: RootState) => state.user.activePlan);
  const isFreePlan = activePlanName === planDetails.free;
  const dispatch = useDispatch();
  const reAuditProcess = localStorageMethod('get', 'reaudit_process');
  const isReAuditProcess = reAuditProcess == 'true';
  // User note: "Note for monthly and yearly detection add variable for now let timeline = 'monthly'"
  let TIMELINE: any = `${stripeSubscriptionDetails?.interval}ly`;

  // Default: start at 5 (in the "Most Popular" 4-10 range)
  const [slots, setSlots] = useState<number>(4);
  const [loading, setLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState<any>(null);
  const [openCardModal, setOpenCardModal] = useState(false);
  const [cardDetails, setCardDetails] = useState({});
  const [isCardUpdated, setIsCardUpdated] = useState(false);
  const [aiSiteOpen, setAiSiteOpen] = useState(true);
  const [unlockingOpen, setUnlockingOpen] = useState(true);

  const totalScans =
    scanDetailRedux?.['scan'] ||
    subscription?.scan ||
    stripeSubscriptionDetails?.plan_name?.scan ||
    0;

  const totalAiSlots = searchesDetails?.totalAiSlots || 0;

  const MAX_SLOTS = isFreePlan ? 20 - totalAiSlots : totalScans - totalAiSlots;
  //const MAX_SLOTS = 0;

  const planFeatures = useMemo(() => {
    if (isFreePlan) {
      return [
        'AI visibility tracking',
        'Full site-wide audit',
        'SEO tasks & fixes',
        'Ongoing monitoring'
      ];
    } else {
      return [
        `AI visibility tracking for ${slots} websites`,
        'Track across ChatGPT, Claude, Gemini & Perplexity',
        'Track across multiple keywords & topics',
        'Ongoing AI visibility monitoring'
      ];
    }
  }, [slots, isFreePlan]);

  useEffect(() => {
    if (MAX_SLOTS <= 0) {
      if (slots !== 0) setSlots(0);
    } else if (slots > MAX_SLOTS) {
      setSlots(MAX_SLOTS);
    } else if (slots === 0 && MAX_SLOTS > 0) {
      setSlots(Math.min(4, MAX_SLOTS));
    }
  }, [MAX_SLOTS, slots]);

  const pricing = useMemo(() => {
    const pricePerSlot = getPricePerSlot(slots, TIMELINE);
    // Base price comparison for savings logic:
    // Monthly base is $50. Yearly base is $450.
    const basePrice = TIMELINE === 'monthly' ? 50 : 450;
    const savings = basePrice - pricePerSlot;

    return {
      pricePerSlot,
      total: slots * pricePerSlot,
      timelineLabel: TIMELINE === 'monthly' ? '/month' : '/year',
      savings
    };
  }, [slots]);

  const activeTierIndex = useMemo(() => {
    if (slots <= 0) return -1;
    if (slots >= 1 && slots <= 3) return 0;
    if (slots >= 4 && slots <= 10) return 1;
    return 2; // 11+
  }, [slots]);

  const getSearchesDetail = async () => {
    const response: any = await getCreditDetails();
    if (response) {
      updateSearchesDetailsAndLimits(response);
    }
  };

  const callBuyAiAddon = async () => {
    try {
      setLoading(true);
      const response: any = await buyAiAddon({ slots });

      if (response?.status === 'success') {
        dispatch(setIsAiAddonPurchased(true));
        await getSearchesDetail();
        await getSubScriptionDetails();
        if (isReAuditProcess) {
          await getSiteDetail();
        }
        // if (isFreePlan && location.pathname === paths?.aiVisibility) {
        //   await getSubScriptionDetails();
        // }
      }

      setResponseStatus(response);
      dispatch(setAiAddonMessage(response));

      setTimeout(() => {
        setResponseStatus(null);
        dispatch(setAiAddonMessage(null));
        if (response?.status === 'success') {
          handleClose();
        }
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCardUpdated) {
      callBuyAiAddon();
      setIsCardUpdated(false);
    }
  }, [isCardUpdated]);

  const handleTierClick = (index: number) => {
    if (index === 0) setSlots(1);
    else if (index === 1) setSlots(4); // Middle of 4-10
    else setSlots(11); // Start of 11+
  };

  const getSiteDetail = async () => {
    let siteDetailResponse: any = await siteDetail(siteId, compare);
    if (siteDetailResponse?.response) {
      // Update website monitoring details in the Redux store.
      dispatch(setWebsiteMonitoringDetails(siteDetailResponse?.response));
    }
  };

  const getSubScriptionDetails = async () => {
    const subscriptionDetailsStripe = await getSubscriptionDetailsStripe();
    if (subscriptionDetailsStripe != undefined) {
      dispatch(setStateStripeSubscription(subscriptionDetailsStripe));
    } else {
      dispatch(setStateStripeSubscription({}));
    }
  };

  const content = aiVisibilityContent;

  return (
    <Box sx={sx.container}>
      {/* STICKY HEADER */}
      <Box sx={sx.stickyHeader}>
        <Box
          sx={{ ...sx.headerSection, paddingBottom: '8px', paddingTop: '16px' }}
        >
          <Box sx={sx.headerContent}>
            {/* 1. Title and chip on left side */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <Typography sx={sx.title}>
                {'Boost Your AI Visibility'}
              </Typography>
              {TIMELINE === 'yearly' && (
                <Box
                  sx={{
                    ...sx?.lockIconBoxSx
                  }}
                >
                  <Typography sx={paidPlanOnBoardingSxs?.unlockTextSx}>
                    {'Save 3 months with annual'}
                  </Typography>
                </Box>
              )}
            </Box>
            {/* Close icon on right side */}
            <IconButton
              onClick={handleClose}
              sx={viewScanDialogSx.closeIconBtnSx}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {/* 2. Description on right side */}
          <Typography sx={sx.subtitle}>
            Track your brand across AI answers. Choose your slots below.
          </Typography>
        </Box>
        {/* 3. Divider */}
        <Divider />
      </Box>

      {/* SCROLLABLE MIDDLE CONTENT */}
      <Box sx={sx.scrollableContent}>
        {/* 4. "What each AI site includes" box */}
        {isFreePlan && (
          <Box sx={{ padding: '0px 24px 0px 24px' }}>
            <Box sx={{ ...upgradeWarningPlanSx?.listBox }}>
              {/* Collapsible title row */}
              <Box
                onClick={() => setAiSiteOpen((prev) => !prev)}
                sx={sx.collapsibleTitleRow}
              >
                <Typography
                  sx={{
                    color: 'var(--color-primary)',
                    lineHeight: 'var(--line-height-28)',
                    fontSize: 'var(--font-size-18)',
                    fontWeight: 700
                  }}
                >
                  {'What each AI site includes'}
                </Typography>
                <Box sx={sx.collapseChevronWrapper}>
                  {aiSiteOpen ? (
                    <KeyboardArrowUpIcon sx={sx.collapseChevron} />
                  ) : (
                    <KeyboardArrowDownIcon sx={sx.collapseChevron} />
                  )}
                </Box>
              </Box>
              <Collapse in={aiSiteOpen}>
                <List sx={{ pt: 0 }}>
                  {content.featureList.map((feature, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        marginBottom: '10px',
                        ...upgradeWarningPlanSx?.listItem
                      }}
                    >
                      <ListItemIcon sx={upgradeWarningPlanSx?.listItemIcon}>
                        <img
                          draggable={false}
                          src={
                            process.env.PUBLIC_URL +
                            `/static/images/icons/blue_check.svg`
                          }
                          alt="Check Icon"
                        />
                      </ListItemIcon>
                      <ListItemText
                        sx={sx?.aiSiteListItemText}
                        primary={feature}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          </Box>
        )}

        {/* 8. "You're unlocking" box */}
        <Box sx={{ mb: 1, mt: 2, ...sx.howItWorksSection(unlockingOpen) }}>
          {/* Collapsible title row */}
          <Box
            onClick={() => setUnlockingOpen((prev) => !prev)}
            sx={sx.collapsibleTitleRow}
          >
            <Typography sx={sx.howItWorksTitle}>You're unlocking:</Typography>
            <Box sx={sx.collapseChevronWrapper}>
              {unlockingOpen ? (
                <KeyboardArrowUpIcon sx={sx.collapseChevron} />
              ) : (
                <KeyboardArrowDownIcon sx={sx.collapseChevron} />
              )}
            </Box>
          </Box>
          <Collapse in={unlockingOpen}>
            <List sx={{ pt: 0 }}>
              {planFeatures.map((feature, index) => (
                <ListItem
                  key={index}
                  sx={{
                    marginBottom: '10px',
                    ...upgradeWarningPlanSx?.listItem
                  }}
                >
                  <ListItemIcon sx={upgradeWarningPlanSx?.listItemIcon}>
                    <img
                      draggable={false}
                      src={
                        process.env.PUBLIC_URL +
                        `/static/images/icons/blue_check.svg`
                      }
                      alt="Check Icon"
                    />
                  </ListItemIcon>
                  <ListItemText sx={sx?.listItemText} primary={feature} />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>

        {/* Divider after "What each AI site includes" */}
        {/*<Divider />*/}

        {/* 5. PRICING TIERS - Changed to vertical structure */}
        <Box sx={sx.pricingVerticalContainer}>
          {[
            {
              label: '1–3 Sites',
              price: getPricePerSlot(1, TIMELINE),
              tag: 'For testing AI visibility',
              minReq: 1,
              discount: '0%',
              checktext: getPricePerSlot(1, 'monthly')
            },
            {
              label: '4–10 Sites',
              price: getPricePerSlot(4, TIMELINE),
              tag: 'Best for growing brands',
              minReq: 4,
              discount: '20%',
              checktext: getPricePerSlot(4, 'monthly')
            },
            {
              label: '10+ Sites',
              price: getPricePerSlot(11, TIMELINE),
              tag: 'For agencies and multi-brands',
              minReq: 11,
              discount: '40%',
              checktext: getPricePerSlot(11, 'monthly')
            }
          ].map((tier, index) => {
            const isActive = activeTierIndex === index;
            const isDisabled = MAX_SLOTS < tier.minReq;

            return (
              <Box
                key={tier.label}
                onClick={() => {
                  if (!isDisabled) handleTierClick(index);
                }}
                sx={sx.pricingCardVertical(isActive, isDisabled)}
              >
                {/* Left side: Check icon + Content */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flex: 1
                  }}
                >
                  {/* Check icon */}
                  <Box sx={{ width: 24, height: 24, marginTop: '2px' }}>
                    {isActive && (
                      <img
                        draggable={false}
                        src={
                          process.env.PUBLIC_URL +
                          `/static/images/icons/checkmark-circle.svg`
                        }
                        alt="Check Icon"
                      />
                    )}
                  </Box>

                  {/* Tag (as title) and Label (as subtitle) */}
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={sx.pricingTagVertical(isActive)}>
                      {tier.tag}
                    </Typography>
                    <Typography sx={sx.pricingLabelVertical(isActive)}>
                      {tier.label}
                    </Typography>
                  </Box>
                </Box>

                {/* Right side: Price, /month text, and discount chip */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    gap: '4px'
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    {/* Price amount */}
                    <Typography sx={sx.pricingPriceVertical(isActive)}>
                      $
                      {TIMELINE === 'monthly'
                        ? tier.price
                        : (tier.price / 12).toFixed(2)}
                      {TIMELINE === 'yearly' && (
                        <span
                          style={{
                            textAlign: 'center',
                            fontFamily: 'Inter',
                            fontSize: 'var(--font-size-14)',
                            fontStyle: 'normal',
                            lineHeight: 'var(--line-height-20)',
                            color: 'var(--color-text-mid)',
                            fontWeight: 400,
                            marginLeft: '4px'
                          }}
                        >
                          <s>{`$${tier?.checktext?.toFixed(2)}`}</s>
                        </span>
                      )}
                    </Typography>

                    {/* /month text below price */}
                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 400,
                        color: 'var(--color-text-tertiary)',
                        lineHeight: 'var(--line-height-20)'
                      }}
                    >
                      {isFreePlan ? '/site/month' : '/month'}
                    </Typography>
                  </Box>

                  {/* Discount chip below /month */}
                  {tier.discount !== '0%' && (
                    <Box
                      sx={{
                        ...UpgradeAgencyPlanModalSxs?.discountBox
                      }}
                    >
                      <span style={UpgradeAgencyPlanModalSxs?.discountSpan}>
                        {`Save ${tier?.discount}`}
                      </span>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* 6. Number of Websites to Unlock */}
        <Box
          sx={{
            ...sx.bodySection,
            paddingBottom: '10px',
            paddingTop: '0px !important'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Typography sx={sx.sectionTitle}>
              {isFreePlan
                ? 'Number of Websites to Unlock:'
                : 'Number of AI Visibility Sites:'}
            </Typography>
            {/* COUNTER BOX */}
            <Box sx={sx.counterContainer}>
              <Box
                component="button"
                disabled={slots <= MIN_SLOTS || isMember}
                onClick={() => setSlots((s) => Math.max(MIN_SLOTS, s - 1))}
                sx={sx.counterBtn}
              >
                −
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={sx.counterValue}>{Math.max(0, slots)}</Box>
              <Divider orientation="vertical" flexItem />
              <Box
                component="button"
                disabled={slots >= MAX_SLOTS || isMember}
                onClick={() => setSlots((s) => Math.min(MAX_SLOTS, s + 1))}
                sx={sx.counterBtn}
              >
                +
              </Box>
            </Box>
          </Box>

          {/* 7. Note */}
          <Typography sx={sx.note(MAX_SLOTS)}>
            {MAX_SLOTS <= 0
              ? 'Note: You already have all the available sites for your current plan.'
              : isFreePlan
              ? 'Note: Each website includes AI visibility, site-wide audits, and SEO tasks.'
              : 'Note: 1 site = AI Visibility tracking for 1 website in your audit. You can only purchase AI sites up to the number of websites in your plan.'}
          </Typography>
        </Box>

        {/* 9. "You selected:" box */}
        {/*<Box sx={{ ...sx.bodySection, pb: 1 }}>
          <Box sx={sx.summarySection}>
            <Typography sx={sx.summaryTitle}>
              You selected:{' '}
              <Box component="span" fontWeight={400}>
                {slots} sites
              </Box>
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexDirection: 'row-reverse'
              }}
            >
              <Typography sx={sx.summaryPricePerSlot}>
                ${pricing.pricePerSlot}{' '}
                <Box
                  component="span"
                  sx={{ fontSize: 16, fontWeight: 400, color: 'var(--color-text-secondary)' }}
                >
                  per site
                </Box>
              </Typography>

              <Typography sx={sx.summaryTotal}>
                Total: ${pricing.total}{' '}
                <Box
                  component="span"
                  sx={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-secondary)' }}
                >
                  {pricing.timelineLabel}
                </Box>
              </Typography>
            </Box>
          </Box>
        </Box>*/}
      </Box>

      {/* STICKY FOOTER */}
      <Box sx={sx.stickyFooter}>
        <Divider />

        {/* 10. Button */}
        <Box sx={sx.bodySection}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexDirection: 'row-reverse',
              marginBottom: '8px'
            }}
          >
            <Typography sx={sx.summaryPricePerSlot}>
              ${pricing.pricePerSlot}{' '}
              <Box
                component="span"
                sx={{ fontSize: 16, fontWeight: 400, color: 'var(--color-text-secondary)' }}
              >
                per site
              </Box>
            </Typography>

            <Typography sx={sx.summaryTotal}>
              Total: ${pricing.total}{' '}
              <Box
                component="span"
                sx={{ fontSize: 14, fontWeight: 400, color: 'var(--color-text-secondary)' }}
              >
                {pricing.timelineLabel}
              </Box>
            </Typography>
          </Box>
          {responseStatus?.status && (
            <CommonAlert
              sx={{ mb: 1 }}
              severity={responseStatus.status}
              message={responseStatus.message}
            />
          )}

          <Button
            fullWidth
            variant="contained"
            sx={sx.cta}
            onClick={() => {
              if (
                isFreePlan &&
                totalAiSlots === 0 &&
                stripeSubscriptionDetails?.subscription_id &&
                !stripeSubscriptionDetails?.hasPaymentMethod
              ) {
                setOpenCardModal(true);
              } else {
                callBuyAiAddon();
              }
            }}
            disabled={loading || slots <= 0 || isMember}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isFreePlan
                  ? 'Unlock AI Visibility & Audits'
                  : 'Activate AI Visibility'}
              </Box>
            )}
          </Button>
        </Box>
      </Box>

      <CommonModal
        open={openCardModal}
        handCloseModal={() => setOpenCardModal(false)}
        title="Enter Card Details"
        closebtn={false}
      >
        <EditPayment
          handCloseModal={() => setOpenCardModal(false)}
          setCardDetails={setCardDetails}
          closeBtn=""
          setIsCardUpdated={setIsCardUpdated}
        />
      </CommonModal>
    </Box>
  );
};

/* ===================== STYLES ===================== */

const sx = {
  container: {
    width: '100%',
    bgcolor: 'var(--color-white)',
    borderRadius: 2,
    mx: 'auto',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '90vh',
    overflow: 'hidden'
  },

  stickyHeader: {
    position: 'sticky',
    top: 0,
    bgcolor: 'var(--color-white)',
    zIndex: 10,
    borderTopLeftRadius: 2,
    borderTopRightRadius: 2
  },

  scrollableContent: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: '6px'
    },
    '&::-webkit-scrollbar-track': {
      background: 'var(--color-bg-grey3)'
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'var(--color-text-grey3)',
      borderRadius: '3px'
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'var(--color-text-medium-grey)'
    }
  },

  stickyFooter: {
    position: 'sticky',
    bottom: 0,
    bgcolor: 'var(--color-white)',
    zIndex: 10,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2
  },

  headerSection: {
    p: 3,
    pb: 2
  },

  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
    //mb: 0.5
  },

  title: {
    fontSize: 20,
    fontWeight: 700,
    color: THEME_COLOR,
    textAlign: 'left'
  },

  subtitle: {
    fontSize: 16,
    color: 'var(--color-text-tertiary)',
    textAlign: 'left',
    fontWeight: 500,
    lineHeight: 'var(--line-height-22)',
    mt: 0 // Reduced spacing
  },

  note: (maxSlots: number) => ({
    fontSize: 14,
    color: maxSlots <= 0 ? 'var(--color-error-dark)' : 'var(--color-text-tertiary)',
    textAlign: 'left',
    fontWeight: 400,
    lineHeight: 'var(--line-height-20)',
    mt: 1 // Reduced spacing
  }),

  bodySection: {
    p: 3,
    pt: 2
  },

  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: THEME_COLOR
    //mb: 1
  },

  counterContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    overflow: 'hidden',
    //width: '240px',
    //mx: 'auto',
    maxHeight: '40px'
  },

  counterBtn: {
    border: 'none',
    bgcolor: 'var(--color-bg-subtle)', // Light gray background
    color: 'var(--color-text-dark)',
    cursor: 'pointer',
    width: 60,
    height: 40,
    fontSize: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    p: 0,
    '&:hover': {
      bgcolor: 'var(--color-bg-muted)'
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed'
    }
  },

  counterValue: {
    flex: 1,
    textAlign: 'center',
    fontSize: 18,
    fontWeight: 700,
    bgcolor: 'var(--color-white)',
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: '16px',
    paddingRight: '16px'
  },

  sliderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    marginTop: '12px'
  },

  sliderLabelText: {
    fontSize: 14,
    fontWeight: 500,
    color: 'var(--color-text-dark)'
  },

  slider: {
    color: THEME_COLOR,
    height: 8,
    '& .MuiSlider-thumb': {
      width: 24,
      height: 24,
      border: '3px solid white',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    },
    '& .MuiSlider-track': {
      border: 'none'
    }
  },

  summarySection: {
    // "in you selected card remove bg color"
    // "add border to it" (assumed from previous context or generic card style, but explicitly said "remove bg color")
    // Let's keep a border to define the section, but white bg.
    border: '1px solid #E4E6EE',
    borderRadius: '8px',
    p: 2,
    marginBottom: '12px',
    bgcolor: 'var(--color-white)'
  },

  summaryTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: THEME_COLOR
  },

  summaryPricePerSlot: {
    fontSize: 18,
    fontWeight: 700,
    color: 'var(--color-text-darkest)',
    lineHeight: 'var(--line-height-24)'
    //mb: 0.5
  },

  summaryTotal: {
    fontSize: 20, // Increased size
    fontWeight: 800,
    color: 'var(--color-text-darkest)'
  },

  pricingGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: 1.5,
    p: 3,
    pt: 2,
    pb: 0
  },

  // New vertical pricing container
  pricingVerticalContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
    p: 3,
    pt: 2,
    pb: 2
  },

  // New vertical pricing card
  pricingCardVertical: (active: boolean, disabled: boolean) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    px: 2,
    py: 1.5,
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    border: active ? '2px solid var(--color-secondary)' : '2px solid var(--color-border-medium)',
    bgcolor: 'var(--color-white)',
    color: THEME_COLOR,
    transition: 'all 0.2s ease',
    minHeight: 60
  }),

  // New vertical pricing label (now subtitle)
  pricingLabelVertical: (active: boolean) => ({
    fontSize: 14,
    fontWeight: 400,
    color: 'var(--color-text-tertiary)',
    textAlign: 'left',
    lineHeight: 'var(--line-height-20)'
  }),

  // New vertical pricing price
  pricingPriceVertical: (active: boolean) => ({
    fontSize: 18,
    fontWeight: 600,
    color: THEME_COLOR,
    textAlign: 'right',
    lineHeight: 'var(--line-height-24)'
  }),

  // New vertical pricing tag (now main title)
  pricingTagVertical: (active: boolean) => ({
    fontSize: 16,
    color: THEME_COLOR,
    fontWeight: 600,
    lineHeight: 'var(--line-height-24)',
    textAlign: 'left'
  }),

  pricingCard: (active: boolean, disabled: boolean) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center', // Center content vertically
    px: 1,
    py: 2,
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    pointerEvents: disabled ? 'none' : 'auto',
    border: active ? '2px solid var(--color-secondary)' : '2px solid var(--color-border-medium)',
    bgcolor: active && !disabled ? 'var(--color-white)' : 'var(--color-white)', // Selected = Dark Blue (similar to screenshot)
    color: THEME_COLOR,
    position: 'relative',
    transition: 'all 0.2s ease',
    gap: '6px',
    minHeight: 120 // ensure uniform height
    //boxShadow:
    //  active && !disabled ? '0px 4px 12px rgba(18, 2, 66, 0.4)' : 'none'
  }),

  pricingLabel: (active: boolean) => ({
    fontSize: 16,
    fontWeight: 600,
    //color: 'var(--color-text-dark)',
    color: THEME_COLOR,
    textAlign: 'center'
  }),

  pricingPrice: (active: boolean) => ({
    fontSize: 22,
    fontWeight: 700,
    //color: 'var(--color-text-darkest)',
    color: THEME_COLOR,
    mt: 0.5,
    mb: 0.5,
    textAlign: 'center'
  }),

  pricingTag: (active: boolean) => ({
    fontSize: 14,
    //color: 'var(--color-text-tertiary)',
    color: THEME_COLOR,
    mt: 'auto', // Push to bottom if flex
    fontWeight: 500,
    lineHeight: 'var(--line-height-16)'
  }),

  popularChip: {
    bgcolor: 'var(--color-white)', // "box bg color white"
    color: THEME_COLOR, // "text color them wise blue" (using the blue from active state)
    fontSize: 14,
    fontWeight: 600,
    lineHeight: 'var(--line-height-16)',
    px: 1.5,
    py: 0.5,
    borderRadius: '12px',
    mt: 'auto',
    border: '1px solid rgba(23, 92, 211, 0.2)'
  },

  // New "How this add-on works" section
  howItWorksSection: (unlockingOpen: boolean) => ({
    backgroundColor: 'var(--color-bg-light-purple)',
    borderRadius: '8px',
    padding: !unlockingOpen ? '0px 16px 0px 16px' : '0px 16px 0px 16px',
    mx: 3
  }),

  howItWorksTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: THEME_COLOR,
    lineHeight: 'var(--line-height-24)'
  },

  /** Clickable row holding the section title + collapse chevron */
  collapsibleTitleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    py: '8px',
    userSelect: 'none'
  },

  /** Circular hover area around the chevron */
  collapseChevronWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    flexShrink: 0,
    transition: 'background 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(18, 2, 66, 0.08)'
    }
  },

  /** Medium chevron icon, theme colour */
  collapseChevron: {
    fontSize: '22px',
    color: THEME_COLOR
  },

  cta: {
    height: 52,
    borderRadius: '6px',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 16,
    boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)'
  },
  checkIcon: {
    position: 'absolute',
    top: '10px',
    right: '10px'
  },
  lockIconBoxSx: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    background: 'var(--color-secondary)',
    padding: '4px 8px 4px 8px',
    borderRadius: '100px'
  },
  listItemText: {
    '& .MuiTypography-root': {
      fontFamily: 'Inter',
      fontSize: 'var(--font-size-14)',
      fontWeight: 600,
      lineHeight: 'var(--line-height-20)',
      textAlign: 'left'
    },
    color: 'var(--color-primary)'
  },
  aiSiteListItemText: {
    '& .MuiTypography-root': {
      fontFamily: 'Inter',
      fontSize: 'var(--font-size-16)',
      fontWeight: 600,
      lineHeight: 'var(--line-height-20)',
      textAlign: 'left'
    },
    color: 'var(--color-primary)'
  }
};
